import { useMemo } from 'react';
import {
  Role,
  Module,
  Permission,
  hasPermission,
  canRead,
  canCreate,
  canEdit,
  canDelete,
  canSchedule,
  canApprove,
  canReject,
  getAccessibleModules,
  getModulePermissions,
  getNavigationItems,
} from '@/config/permissions';
import { useAuthStore } from './useAuthStore';

// Hook to get current user role from auth store
export const useUserRole = (): Role => {
  const { user } = useAuthStore();
  
  if (user && user.role) {
    // Map backend role to RBAC role
    const roleMap: Record<string, Role> = {
      'ADMIN': 'ADMINISTRATOR',
      'ADMINISTRATOR': 'ADMINISTRATOR',
      'ABM_MANAGER': 'ABM_MANAGER',
      'ABM_OPS': 'ABM_OPERATIONS',
      'ABM_OPERATIONS': 'ABM_OPERATIONS',
      'MDA': 'MDA',
      'SALES': 'SALES',
    };
    
    return roleMap[user.role] || 'SALES'; // Default to most restrictive
  }
  
  return 'SALES'; // Default to most restrictive
};

// Main permission hook
export const usePermissions = (module?: Module) => {
  const userRole = useUserRole();
  const customPerms = useAuthStore((s) => s.user?.customPermissions) as
    | { module: string; permissions: string[] }[] | null | undefined;

  // Per-user override map. null => fall back to role defaults.
  // Admins are never overridden, so they can't be locked out.
  const customMap = useMemo(() => {
    if (userRole === 'ADMINISTRATOR' || !Array.isArray(customPerms)) return null;
    const m: Partial<Record<Module, Permission[]>> = {};
    for (const e of customPerms) {
      if (e && e.module) m[e.module as Module] = (e.permissions || []) as Permission[];
    }
    return m;
  }, [userRole, customPerms]);

  const has = (mod: Module, permission: Permission): boolean => {
    if (customMap) return (customMap[mod] || []).includes(permission);
    return hasPermission(userRole, mod, permission);
  };

  const permissions = useMemo(() => {
    if (!module) {
      return {
        canRead: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canSchedule: false,
        canApprove: false,
        canReject: false,
      };
    }

    return {
      canRead: has(module, 'read'),
      canCreate: has(module, 'create'),
      canEdit: has(module, 'edit'),
      canDelete: has(module, 'delete'),
      canSchedule: has(module, 'schedule'),
      canApprove: has(module, 'approve'),
      canReject: has(module, 'reject'),
    };
  }, [userRole, module, customMap]);

  const hasAccess = useMemo(() => {
    if (!module) return false;
    return has(module, 'read');
  }, [userRole, module, customMap]);

  const checkPermission = (permission: Permission): boolean => {
    if (!module) return false;
    return has(module, permission);
  };

  const accessibleModules = useMemo(() => {
    if (!customMap) return getAccessibleModules(userRole);
    return (Object.keys(customMap) as Module[]).filter((m) => (customMap[m] || []).includes('read'));
  }, [userRole, customMap]);

  const modulePermissions = useMemo(() => {
    if (!module) return [];
    if (customMap) return customMap[module] || [];
    return getModulePermissions(userRole, module);
  }, [userRole, module, customMap]);

  const navigationItems = useMemo(() => {
    return getNavigationItems(userRole);
  }, [userRole]);

  return {
    userRole,
    ...permissions,
    hasAccess,
    checkPermission,
    accessibleModules,
    modulePermissions,
    navigationItems,
  };
};

// Hook for role-specific UI display
export const useRoleInfo = () => {
  const userRole = useUserRole();
  
  return {
    role: userRole,
    isAdmin: userRole === 'ADMINISTRATOR',
    isABMManager: userRole === 'ABM_MANAGER',
    isABMOperations: userRole === 'ABM_OPERATIONS',
    isMDA: userRole === 'MDA',
    isSales: userRole === 'SALES',
  };
};

// Returns an effective `canRead(module)` checker for the current user that
// respects per-user customPermissions (admins are never restricted).
// Use this for filtering nav menus and gating routes.
export const useModuleAccess = () => {
  const userRole = useUserRole();
  const customPerms = useAuthStore((s) => s.user?.customPermissions) as
    | { module: string; permissions: string[] }[] | null | undefined;

  const customMap = useMemo(() => {
    if (userRole === 'ADMINISTRATOR' || !Array.isArray(customPerms)) return null;
    const m: Partial<Record<Module, Permission[]>> = {};
    for (const e of customPerms) {
      if (e && e.module) m[e.module as Module] = (e.permissions || []) as Permission[];
    }
    return m;
  }, [userRole, customPerms]);

  return useMemo(() => {
    return (module: Module): boolean => {
      if (customMap) return (customMap[module] || []).includes('read');
      return canRead(userRole, module);
    };
  }, [userRole, customMap]);
};

export default usePermissions;
